import { useCallback, useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { AddTeamMemberModal } from '@/components/team/AddTeamMemberModal';
import type { AppUser } from '@/types';

export function TeamPage() {
  const { appUser } = useAuth();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const loadUsers = useCallback(() => {
    if (!appUser?.companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, 'users'),
      where('companyId', '==', appUser.companyId)
    );
    getDocs(q).then((snap) => {
      setUsers(
        snap.docs.map((d) => ({
          uid: d.id,
          ...d.data(),
          createdAt: d.data().createdAt ?? '',
        })) as AppUser[]
      );
      setLoading(false);
    });
  }, [appUser?.companyId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const roleLabel = (r: string) => (r === 'admin' ? 'Administrador' : r === 'recruiter' ? 'Reclutador' : 'Solo lectura');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Equipo</h1>
        {appUser?.companyId && (
          <Button onClick={() => setAddModalOpen(true)}>Invitar usuario</Button>
        )}
      </div>
      {loading ? (
        <p className="text-muted-foreground">Cargando...</p>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Usuarios de la empresa</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-medium">Nombre</th>
                  <th className="pb-2 font-medium">Email</th>
                  <th className="pb-2 font-medium">Rol</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.uid} className="border-b">
                    <td className="py-2">{u.displayName || '—'}</td>
                    <td className="py-2">{u.email}</td>
                    <td className="py-2">{roleLabel(u.role)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {appUser?.companyId && (
        <AddTeamMemberModal
          open={addModalOpen}
          onOpenChange={setAddModalOpen}
          companyId={appUser.companyId}
          onSaved={loadUsers}
        />
      )}
    </div>
  );
}
